import { apiEnums, useApiClient, type ApiModel } from '@nodal/api';
import { queryKeys } from '@nodal/core/consts/query';
import { useScreening } from '@nodal/core/flows/Screening';
import { InsuranceReview } from '@nodal/core/flows/Screening/InsuranceReview/InsuranceReview';
import { useInsuranceReview } from '@nodal/core/flows/Screening/InsuranceReview/useInsuranceReview';
import { useGooglePlacesApi } from '@nodal/core/hooks/useGooglePlacesApi';
import { useUploadInsuranceDocuments } from '@nodal/core/hooks/useUploadInsuranceDocuments';
import { useUser } from '@nodal/core/hooks/useUser';
import { t } from '@nodal/i18n';
import { useConfirmationDialog } from '@nodal/uikit/components/ConfirmationDialog';
import { LoadingScreen } from '@nodal/uikit/components/LoadingScreen';
import { useQuery } from 'react-query';

const title = t('Insurance Review');
const description = t(
  'Please provide information below to help us evaluate your health insurance coverage as it relates to your surrogacy',
);

export const InsuranceReviewConnected = () => {
  const placesApiReady = useGooglePlacesApi();
  const { currentStep, submit } = useScreening();
  const confirm = useConfirmationDialog();
  const apiClient = useApiClient();
  const { user } = useUser();

  const { data: insuranceDocuments } = useQuery(
    queryKeys.insuranceDocumentsList,
    async () => {
      const response =
        await apiClient.api.MedicalReviewsApi.medicalReviewsInsuranceDocumentsOwnList();
      return {
        ...response,
        data:
          response.data?.filter(
            (doc) =>
              doc.document_type ===
              apiEnums.MedicalDocumentDocumentTypeEnum.SurroInsDoc,
          ) || [],
      };
    },
  );

  const { data: incomeDocuments } = useQuery(
    queryKeys.incomeDocumentsList,
    async () => {
      const response =
        await apiClient.api.MedicalReviewsApi.medicalReviewsInsuranceDocumentsOwnList();
      return {
        ...response,
        data:
          response.data?.filter(
            (doc) =>
              doc.document_type ===
              apiEnums.MedicalDocumentDocumentTypeEnum.SurroIncomeDoc,
          ) || [],
      };
    },
  );

  const { initialValues, onSubmit, validationSchema } = useInsuranceReview({
    submit,
    screenId: currentStep?.id,
    contentObject:
      currentStep?.content_object as ApiModel.InsuranceReviewScreen,
  });

  const { onDelete, onUpload, uploadedFiles, disabled } =
    useUploadInsuranceDocuments({
      documents: insuranceDocuments?.data || [],
      confirm,
      documentType: apiEnums.MedicalDocumentDocumentTypeEnum.SurroInsDoc,
      medicalReviewId: user?.medical_review_id,
    });

  const {
    onDelete: onDeleteIncome,
    onUpload: onUploadIncome,
    uploadedFiles: uploadedIncomeFiles,
    disabled: incomeDisabled,
  } = useUploadInsuranceDocuments({
    documents: incomeDocuments?.data || [],
    confirm,
    documentType: apiEnums.MedicalDocumentDocumentTypeEnum.SurroIncomeDoc,
    medicalReviewId: user?.medical_review_id,
    queryKey: queryKeys.incomeDocumentsList,
  });

  if (!placesApiReady || !currentStep) {
    return <LoadingScreen />;
  }

  return (
    <InsuranceReview
      initialValues={{
        ...initialValues,
        id: currentStep.id,
      }}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      title={title}
      description={description}
      editable
      previewPhotoEnabled={false}
      documentsControl={{
        onUpload,
        onDelete,
        uploadedFiles,
        disabled,
      }}
      incomeDocumentsControl={{
        onUpload: onUploadIncome,
        onDelete: onDeleteIncome,
        uploadedFiles: uploadedIncomeFiles,
        disabled: incomeDisabled,
      }}
    />
  );
};
